import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { TrackEventInput } from '@updater/ui-tracker';
import { Flex } from '@updater/ui-uds';
import { NavBar, NavItem, Path } from 'components/nav-bar/NavBar';
import { HeaderNav } from 'components/nav-bar/HeaderNav';
import { useItemsByStatus } from 'flows/core/hooks/use-items-by-status';
import { useMergedPromotions } from 'flows/deals/hooks/use-merged-promotions';
import { usePromotionsQuery } from 'flows/deals/hooks/use-promotions-query';
import { MovingAppBrandedHeader } from 'components/branded-header';
import { useRouter } from 'next/router';
import React, { PropsWithChildren, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import { useFeature } from '@growthbook/growthbook-react';
import { useViewportSizes } from '@updater/ui-uds';
import { Key, Star, Truck, Tag } from '@phosphor-icons/react';

const ErrorPath = '/_error';

export const tabRoutes: Path[] = [
  '/',
  '/today',
  '/moving',
  '/deals',
  ErrorPath,
];

const InnerContent = styled(Flex)`
  ${({ theme }) => theme.mediaQueries.l} {
    padding: ${({ theme }) => `0 ${theme.space.m}px`};
    max-width: ${({ theme }) => `calc(1440px + ${theme.space.xxl}px)`};
  }
`;

const NavigationLayoutContent: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const route = router.pathname;
  const theme = useTheme();
  const { isMobile } = useViewportSizes();
  const isWhiteLabeled = !!theme.whiteLabelTheme;

  // TODO: change to correct status when needs more status is implemented
  const needsMoreCards = useItemsByStatus('needs-more');

  useTracking<TrackEventInput<unknown>>({
    domain: 'navigation-layout',
    details: {
      path: {
        pathname: route,
      },
    },
  });

  const navigate = (path: Path) => {
    // if the user clicks on the current tab
    if (path === route) return;
    router.push(path as Path);
  };

  const NAV_ITEMS: NavItem[] = useMemo(() => {
    // Error path has no tab items
    if (route === ErrorPath) {
      return [];
    }

    return [
      {
        text: 'Today',
        key: 'today',
        path: '/today',
        icon: <Star />,
        onClick: () => navigate('/today'),
      },
      {
        text: 'Essentials',
        key: 'essentials',
        path: '/',
        dot: needsMoreCards.length > 0,
        icon: <Key />,
        onClick: () => navigate('/'),
      },
      {
        text: 'Moving',
        key: 'moving',
        path: '/moving',
        icon: <Truck />,
        onClick: () => navigate('/moving'),
      },
    ];
  }, [router, `${needsMoreCards}`, route]);

  const { data: promotions, loading: promotionsLoading } = usePromotionsQuery();

  const mergedPromotions = useMergedPromotions(promotions);

  const promotionList = useMemo(() => {
    if (promotionsLoading) return [];
    return mergedPromotions;
  }, [mergedPromotions, promotionsLoading]);

  const { on: dealsFeatureEnabled } = useFeature('deals');

  if (
    promotionList &&
    promotionList.length > 0 &&
    !NAV_ITEMS.find((nav) => nav.key === 'deals') &&
    dealsFeatureEnabled
  ) {
    NAV_ITEMS.push({
      text: 'Deals',
      key: 'deals',
      path: '/deals',
      icon: <Tag />,
      onClick: () => navigate('/deals'),
    });
  }
  const shouldShowNavigationInHeader = isWhiteLabeled && !isMobile;

  return (
    <Flex flexDirection="column">
      <MovingAppBrandedHeader
        navigation={
          shouldShowNavigationInHeader ? (
            <HeaderNav items={NAV_ITEMS} />
          ) : undefined
        }
      />
      <div id="white-label-hero-slot" />
      <InnerContent
        overflow="hidden"
        maxWidth="1440px"
        marginX="auto"
        width="100%"
      >
        {!shouldShowNavigationInHeader && <NavBar items={NAV_ITEMS} />}
        {children}
      </InnerContent>
    </Flex>
  );
};

export const NavigationLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();

  const isTabsLayout =
    tabRoutes.filter((tabRoute) => tabRoute === router?.pathname).length > 0;

  if (isTabsLayout) {
    return <NavigationLayoutContent>{children}</NavigationLayoutContent>;
  }
  return <>{children}</>;
};
